import AnimationButton from '../../atoms/AnimationButton/AnimationButton'
import Constants from '../../../app/utils/constants'
import colors from '../../theme/color'
import viewdetails from '../../../assets/images/industrypage/viewdetails.svg'
import { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'

const IndustryDetails = () => {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null)
  const { industryDetailsCards } = Constants
  const navigate = useNavigate()

  const defaultTopBorder = `linear-gradient(to right, ${colors.purpleAlt}, ${colors.purple}, transparent)`
  const defaultBottomBorder = `linear-gradient(to left, ${colors.purpleAlt}, ${colors.purple}, transparent)`
  const hoverTopBorder = `linear-gradient(to right, ${colors.industryHoverStart}, ${colors.industryHoverEnd})`
  const hoverBottomBorder = `linear-gradient(to right, ${colors.industryHoverStart}, ${colors.industryHoverEnd})`
  const fadeUp = {
    initial: { opacity: 0, y: 80 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: false, margin: '-100px' },
    transition: { duration: 0.7, ease: 'easeIn' as const },
  }

  const getCardRoute = (title: string) => {
    if (title === 'Fintech & Banking') return '/industryfintech'
    if (title === 'Healthcare & Wellness') return '/industryhealthcare'
    return null
  }

  return (
    <section className="w-full">
      <div className="w-full">
        <div className="my-10 flex w-full justify-center px-4 md:mb-14 md:px-8 lg:px-12">
          <div className="w-fit rounded-full p-[2px] font-rajdhani font-semibold bg-[linear-gradient(90.94deg,#745EF0_0%,rgba(116,94,240,0.1)_50%,#745EF0_100%)]">
            <AnimationButton className="font-rajdhani font-semibold tracking-wide text-base md:text-lg lg:text-xl xl:text-2xl 2xl:text-4xl">INDUSTRIES</AnimationButton>
          </div>
        </div>

        <div className="w-full space-y-10">
          {industryDetailsCards.map((card, index) => {
            const isHovered = hoveredCard === index
            const isReverse = index % 2 === 1
            const cardRoute = getCardRoute(card.title)
            const isCardNavigable = Boolean(cardRoute)
            const shouldShake = isHovered && !isCardNavigable

            return (
              <motion.article
                key={`${card.title}-${index}`}
                className={`w-full overflow-hidden ${isCardNavigable ? 'cursor-pointer' : ''}`}
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
                onClick={() => {
                  if (cardRoute) {
                    navigate(cardRoute)
                  }
                }}
                onKeyDown={(event) => {
                  if (!cardRoute) return
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    navigate(cardRoute)
                  }
                }}
                role={isCardNavigable ? 'link' : undefined}
                tabIndex={isCardNavigable ? 0 : -1}
                {...fadeUp}
              >
                <div className="relative h-px w-full shrink-0" aria-hidden>
                  <div
                    className="absolute inset-0"
                    style={{ backgroundImage: defaultTopBorder }}
                  />
                  <div
                    className="absolute inset-0 transition-transform duration-[900ms] ease-out"
                    style={{
                      backgroundImage: hoverTopBorder,
                      transform: isHovered ? 'scaleX(1)' : 'scaleX(0)',
                      transformOrigin: 'left center',
                    }}
                  />
                </div>

                <div
  className={`flex w-full flex-col gap-7 md:gap-4 lg:gap-10 px-4 py-8 md:px-5 lg:px-10 xl:px-15 2xl:px-20 ${
    isReverse ? 'md:flex-row-reverse' : 'md:flex-row'
  } md:items-center`}
                >
                  <div className="flex w-full shrink-0 justify-center md:block md:w-[40%]">
                    <div className="relative mx-auto w-[80%] sm:w-[60%] md:w-full lg:w-full xl:w-[75%] 2xl:w-[70%] aspect-square">
                      <img
                        src={card.image}
                        alt={card.title}
                        className="h-full w-full rounded-xl object-cover object-center"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <img
                          src={card.icon}
                          alt=""
                          aria-hidden
                          className="h-[55%] w-[55%] object-contain drop-shadow-lg"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex w-full flex-col gap-1 text-white md:w-[58%]">
                    <h2 className="font-rajdhani text-2xl font-semibold uppercase leading-tight sm:text-3xl md:text-xl lg:text-4xl xl:text-5xl 2xl:text-5xl">
                      {card.title}
                    </h2>
                    <p className="font-gilroy-medium text-sm leading-relaxed text-gray-300/80 md:text-base lg:text-xl xl:text-2xl 2xl:text-2xl">
                      {card.intro}
                    </p>
                    <h4
                      className="mt-6 md:mt-8 font-rajdhani text-base font-semibold transition-colors duration-[900ms] ease-out sm:text-lg md:text-xl lg:text-2xl xl:text-3xl 2xl:text-4xl"
                      style={{ color: isHovered ? colors.industryHoverStart : colors.purple }}
                    >
                      {card.focusHeading}
                    </h4>
                    <p className="font-gilroy-medium text-xs leading-relaxed text-gray-400 sm:text-sm md:text-base xl:text-xl 2xl:text-2xl">
                      {card.focusText}
                    </p>
                    <motion.span
                      className="group relative mt-8 inline-flex w-fit items-center gap-2 overflow-hidden rounded-full px-4 py-2 font-rajdhani text-xs font-semibold uppercase text-white transition-all duration-[900ms] ease-out sm:gap-3 sm:px-5 md:px-6 md:py-3 md:text-sm lg:text-base xl:text-lg 2xl:text-xl"
                      style={{
                        background: `linear-gradient(90deg, ${colors.purpleAlt}, ${colors.purple})`,
                      }}
                    >
                      <span
                        aria-hidden
                        className="pointer-events-none absolute inset-0 transition-opacity duration-[800ms] ease-out"
                        style={{
                          background: `linear-gradient(90deg, ${colors.industryHoverStart}, ${colors.industryHoverEnd})`,
                          opacity: isHovered ? 1 : 0,
                        }}
                      />
                      <motion.span
                        className="relative z-10 inline-flex items-center gap-2"
                        animate={
                          shouldShake
                            ? { x: [0, -6, 6, -6, 6, 0, 0, -6, 6, -6, 6, 0] }
                            : { x: 0 }
                        }
                        transition={{
                          duration: 1,
                          ease: 'easeIn',
                        }}
                        whileTap={
                          !isCardNavigable
                            ? { x: [0, -6, 6, -6, 6, 0, 0, -6, 6, -6, 6, 0] }
                            : {}
                        }
                      >
                        <span>{card.cta}</span>
                        <img
                          src={viewdetails}
                          alt=""
                          className="h-4 w-4 shrink-0 brightness-0 invert"
                          aria-hidden
                        />
                      </motion.span>
                    </motion.span>
                  </div>
                </div>

                <div className="relative h-px w-full shrink-0" aria-hidden>
                  <div
                    className="absolute inset-0"
                    style={{ backgroundImage: defaultBottomBorder }}
                  />
                  <div
                    className="absolute inset-0 transition-transform duration-[900ms] ease-out"
                    style={{
                      backgroundImage: hoverBottomBorder,
                      transform: isHovered ? 'scaleX(1)' : 'scaleX(0)',
                      transformOrigin: 'left center',
                    }}
                  />
                </div>
              </motion.article>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default IndustryDetails